/**
 * Challenge Card Component
 * Refactored from 1,033 lines to modular structure
 */

import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { View, Text, Animated, Easing, ScrollView, useWindowDimensions } from 'react-native';
import { SoundPressable } from '../../src/shared/components/SoundPressable/SoundPressable';
import { SOUND_IDS } from '../../src/shared/constants/sounds.constants';
import { useChallengePredictions } from '../../src/challenges/hooks/useChallengePredictions';
import { useChallengeExpansion } from '../../src/challenges/hooks/useChallengeExpansion';
import { useChallengeActions } from '../../src/challenges/hooks/useChallengeActions';
import {
  buildParticipants,
  calculateDisplayName,
  canCancelChallenge,
  getDefaultSelectedOpponentId,
  normalizeUserIdForComparison,
} from '../../src/challenges/helpers/challengeCardParticipant.helper';
import { remindChallenge } from '../../src/challenges/helpers/challengeCardApi';
import { useDialog } from '../../src/shared/hooks/useDialog';
import { useApiClient } from '../../helpers/apiClient.helper';
import { getRequiredMarketsPerMatch } from '../../src/challenges/helpers/acceptChallengePicks.helper';
import { splitChallengePicksForDisplay } from '../../src/challenges/helpers/challengePicksDisplay.helper';
import { buildChallengeCardA11yLabel } from '../../src/challenges/helpers/challengeCardA11y.helper';
import { buildMatchByIdFromPicks } from '../../src/challenges/helpers/buildMatchByIdFromPicks.helper';
import { getMatchDisplayStatusUpper } from '../../src/challenges/helpers/matchDisplayStatus.helper';
import { useRouter, useFocusEffect } from 'expo-router';
import { buildGuestSignUpHref } from '../../src/shared/helpers/guestSignUpHref.helper';
import { useChallengesPage } from '../../src/challenges/contexts/ChallengesPageContext';
import {
  GUEST_PENDING_ARENA_PRIMARY_CTA,
  isGuestPendingMonkeyArenaChallengeId,
} from '../../src/challenges/constants/guestPendingArena.constants';
import { OPEN_CHALLENGE_OPPONENT_LABEL } from '../../src/challenges/constants/challenges.constants';
import { isUntakenOpenChallenge } from '../../src/challenges/helpers/openChallengeFace.helper';
import { MONKEY_USER_ID, MONKEY_AVATAR_ID } from '../../constants';
import { DEFAULT_GROUP_IMAGE_SOURCE } from '../../constants/assets'
import { ChallengeCardHeader } from './ChallengeCardHeader';
import { ChallengeCardMatches } from './ChallengeCardMatches';
import { ChallengeCardActions } from './ChallengeCardActions';
import { AcceptChallengePicksModal } from './AcceptChallengePicksModal';
import { ChallengeParticipantSwitcher } from './ChallengeParticipantSwitcher';
import type { ChallengeCardProps, Participant } from '../../src/challenges/types/challengeCard.types';
import type { ChallengeWithParticipantAvatars } from '../../types/challenges';
import type { MatchApiResponse } from '../../src/friends/types/friends.types';
import { getChallengeCardStyles } from '../../src/challenges/styles/challengeCard.styles';
import { useTheme } from '../../src/theme/useTheme';
import { logger } from '../../src/helpers/logger.helper';
import { formatRelativeTime } from '../../src/notifications/helpers/notificationsUi.helper';
import { hasEnoughCoins } from '../../src/shared/helpers/coins.helper';
import { useEconomyStore } from '../../src/shared/stores/economyStore';
import { useUserProfile } from '../../src/shared/contexts/UserProfileContext';
import { ChallengeCardHero } from './ChallengeCardHero/ChallengeCardHero';
import { ChallengeCardStatusBanner } from './ChallengeCardStatusBanner/ChallengeCardStatusBanner';

export const ChallengeCard: React.FC<ChallengeCardProps> = ({
  challenge,
  currentUserId,
  onPress: _onPress,
  onShowMessage,
  onChallengeCancelled,
  variant = 'default',
  sectionBucket,
  testID,
}) => {
  const router = useRouter();
  const isGuestPendingArenaCard = useMemo(
    () => isGuestPendingMonkeyArenaChallengeId(challenge.id),
    [challenge.id],
  );
  const guestSignUpHref = buildGuestSignUpHref();
  const theme = useTheme();
  const userCoins = useEconomyStore((state) => state.coins);
  const { height: windowHeight } = useWindowDimensions();
  const expandedScrollMaxHeight = useMemo(() => windowHeight * 0.45, [windowHeight]);
  const { profile: currentUserProfile } = useUserProfile();
  const currentUserAvatarId = currentUserProfile?.avatarId ?? null;
  const currentUserAvatarConfig = currentUserProfile?.avatarConfig ?? null;
  const styles = useMemo(() => getChallengeCardStyles(theme, { expandedScrollMaxHeight }), [theme, expandedScrollMaxHeight]);

  // Fallback: when matchesMap has no entry for a matchId, use match from challenge.picks[].match (API embeds full match in each pick).
  const { matchesMap, loadChallenges, expandedChallengeId } = useChallengesPage();
  const matches = useMemo(() => {
    const ids = Array.isArray(challenge.matchIds) ? challenge.matchIds : [];
    const picks = Array.isArray(challenge.picks) ? challenge.picks : [];
    const matchByIdFromPicks = buildMatchByIdFromPicks(picks);
    return ids
      .map((id) => (matchesMap[id] as unknown) ?? matchByIdFromPicks[String(id)])
      .filter(Boolean);
  }, [challenge.matchIds, challenge.picks, matchesMap]);

  const showLiveMatchBadge = useMemo(() => {
    if (sectionBucket === 'finished') return false;
    return matches.some((raw) => {
      if (raw == null || typeof raw !== 'object') return false;
      const m = raw as { status?: string; date?: string | Date };
      return getMatchDisplayStatusUpper(m.status, m.date) === 'LIVE';
    });
  }, [matches, sectionBucket]);

  // Manage predictions state (handlePickSelect omitted; modal has its own 1X2 handling)
  const { userPredictions, hasChanges } = useChallengePredictions({
    challenge,
    currentUserId,
  });

  // Manage expansion state (expand when opened from notification deep link)
  const { isExpanded, showAllMatches, toggleExpand, handleShowAllMatches } = useChallengeExpansion(
    expandedChallengeId === challenge.id
  );

  const expandAnim = useRef(new Animated.Value(0)).current;
  const expandedContentStyle = useMemo(
    () => ({
      maxHeight: expandAnim.interpolate({ inputRange: [0, 1], outputRange: [0, 2000] }),
      opacity: expandAnim,
    }),
    [expandAnim]
  );
  useEffect(() => {
    Animated.timing(expandAnim, {
      toValue: isExpanded ? 1 : 0,
      duration: 250,
      easing: Easing.inOut(Easing.ease),
      useNativeDriver: false,
    }).start();
  }, [isExpanded, expandAnim]);

  const participants = useMemo(
    () => buildParticipants(challenge, currentUserId),
    [challenge, currentUserId]
  );

  const otherParticipants = useMemo(() => {
    const normalizedCurrent = currentUserId == null ? '' : normalizeUserIdForComparison(currentUserId);
    const seen = new Set<string>();
    return participants.filter((p) => {
      const id = String(p.id).trim();
      if (!id) return false;
      if (id.includes('@')) return false;
      if (normalizeUserIdForComparison(p.id) === normalizedCurrent) return false;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [participants, currentUserId]);
  const defaultSelectedOpponentId = useMemo(
    () => getDefaultSelectedOpponentId(challenge, currentUserId, otherParticipants),
    [challenge, currentUserId, otherParticipants]
  );
  const [selectedOpponentId, setSelectedOpponentId] = useState<string | null>(null);
  const effectiveSelectedOpponentId = selectedOpponentId ?? defaultSelectedOpponentId;

  useEffect(() => {
    if (!challenge.groupId || otherParticipants.length === 0) return;
    const current = selectedOpponentId ?? defaultSelectedOpponentId;
    const isValid = current && otherParticipants.some((p) => p.id === current);
    if (!current || !isValid) {
      setSelectedOpponentId(defaultSelectedOpponentId);
    }
  }, [challenge.groupId, otherParticipants, defaultSelectedOpponentId, selectedOpponentId]);

  // Group challenge: use embedded groupName from API (no groups query); fallback to challenge.name or 'Group challenge'
  const displayName = useMemo(() => {
    if (challenge.groupId) {
      return challenge.groupName ?? (challenge.name || 'Group challenge');
    }
    return calculateDisplayName(challenge, currentUserId, participants);
  }, [challenge, currentUserId, participants]);

  // Resolve other first so we can hide self from header when there is no real opponent
  const otherParticipant = useMemo(() => {
    const normalizedCurrent = normalizeUserIdForComparison(currentUserId);
    const normalizedCreator = normalizeUserIdForComparison(challenge.creatorId);
    const notMe = (p: Participant) =>
      normalizeUserIdForComparison(p.id) !== normalizedCurrent && !p.isCurrentUser;
    const other = participants.find(
      (p) => normalizeUserIdForComparison(p.id) !== normalizedCreator && notMe(p)
    );
    const candidate = other ?? participants.find(notMe);
    if (candidate != null && normalizeUserIdForComparison(candidate.id) === normalizedCurrent) {
      return undefined;
    }
    return candidate;
  }, [participants, challenge.creatorId, currentUserId]);

  const challengeWithAvatars = challenge as ChallengeWithParticipantAvatars;
  const isOtherParticipantSelf =
    otherParticipant != null &&
    normalizeUserIdForComparison(otherParticipant.id) === normalizeUserIdForComparison(currentUserId);
  // An open-board challenge nobody has taken yet has no second participant by design, so it would
  // otherwise render as a bare-initials "Challenge" circle. Give it the "Anyone" face instead.
  const isUntakenOpen = isUntakenOpenChallenge(challenge);
  const headerDisplayName =
    otherParticipant == null || isOtherParticipantSelf
      ? challenge.groupId
        ? challenge.groupName ?? (challenge.name || 'Group challenge')
        : isUntakenOpen
          ? OPEN_CHALLENGE_OPPONENT_LABEL
          : 'Challenge'
      : displayName;
  const groupImageSource = useMemo(() => {
    if (challenge.groupId || isUntakenOpen) {
      return DEFAULT_GROUP_IMAGE_SOURCE;
    }
    return undefined;
  }, [challenge.groupId, isUntakenOpen]);

  const {
    otherPicksFromPicks,
    otherParticipantPicks,
    opponent1x2ByMatch,
    my1x2OddByMatch,
    my1x2CorrectByMatch,
  } = useMemo(() => {
    const { matchWinnerPicks, otherPicks } = splitChallengePicksForDisplay(challenge.picks);
    const myOther = otherPicks.filter((p) => p.userId === currentUserId);
    const isGroupWithSelection = !!challenge.groupId && !!effectiveSelectedOpponentId;
    const theirOther = isGroupWithSelection
      ? otherPicks.filter((p) => p.userId === effectiveSelectedOpponentId)
      : otherPicks.filter((p) => p.userId !== currentUserId);
    const opponentMap: Record<string, { value: 'home' | 'draw' | 'away'; odd: number; isCorrect?: boolean } | null> = {};
    const opponent1x2Source = isGroupWithSelection
      ? matchWinnerPicks.filter((p) => p.userId === effectiveSelectedOpponentId && p.value != null)
      : matchWinnerPicks.filter((p) => p.userId !== currentUserId && p.value != null);
    opponent1x2Source.forEach((p) => {
      const v = String(p.value).toLowerCase();
      const pick = v === 'draw' ? 'draw' : v === 'away' ? 'away' : 'home';
      const isCorrectSettled =
        p.isCorrectOutcome === true || p.isCorrectOutcome === false ? p.isCorrectOutcome : undefined;
      opponentMap[p.matchId] = {
        value: pick,
        odd: Number(p.odd) || 0,
        isCorrect: isCorrectSettled,
      };
    });
    const myCorrectMap: Record<string, boolean> = {};
    const myOddByMatch: Record<string, number> = {};
    matchWinnerPicks
      .filter((p) => p.userId === currentUserId)
      .forEach((p) => {
        if (p.isCorrectOutcome === true) myCorrectMap[p.matchId] = true;
        else if (p.isCorrectOutcome === false) myCorrectMap[p.matchId] = false;
        const o = Number(p.odd);
        if (Number.isFinite(o) && o > 1) {
          myOddByMatch[p.matchId] = o;
        }
      });
    return {
      otherPicksFromPicks: myOther,
      otherParticipantPicks: theirOther,
      opponent1x2ByMatch: opponentMap,
      my1x2OddByMatch: myOddByMatch,
      my1x2CorrectByMatch: myCorrectMap,
    };
  }, [challenge.picks, challenge.groupId, currentUserId, effectiveSelectedOpponentId]);

  const requiredMarketsByMatch = useMemo(
    () => getRequiredMarketsPerMatch(challenge),
    [challenge]
  );

  const headerOpponentAvatar =
    !challenge.groupId && otherParticipant?.id && !isOtherParticipantSelf
      ? challengeWithAvatars.participantAvatarsById?.[otherParticipant.id]
      : undefined;
  const headerOpponentAvatarId =
    challenge.groupId || isOtherParticipantSelf
      ? undefined
      : otherParticipant?.id === MONKEY_USER_ID
        ? MONKEY_AVATAR_ID
        : (otherParticipant?.id ? challengeWithAvatars.participantAvatarIdsById?.[otherParticipant.id] : undefined);
  const headerOpponentAvatarConfig =
    !challenge.groupId && otherParticipant?.id && !isOtherParticipantSelf
      ? challengeWithAvatars.participantAvatarConfigsById?.[otherParticipant.id] ?? null
      : null;

  // Debug: log diagnostic for finished challenge cards (level controlled by EXPO_PUBLIC_LOG_LEVEL)
  useEffect(() => {
    if (sectionBucket === 'finished') {
      const diagnostic = {
        challengeId: challenge.id,
        groupId: challenge.groupId ?? null,
        creatorId: challenge.creatorId,
        leader: challenge.leader
          ? {
              userId: challenge.leader.userId,
              userName: challenge.leader.userName,
              points: challenge.leader.points,
            }
          : null,
        participantIds: challenge.participantIds ?? [],
        currentUserId,
        participants: participants.map((p) => ({
          id: p.id,
          name: p.name,
          isCurrentUser: p.isCurrentUser,
        })),
        otherParticipant: otherParticipant
          ? { id: otherParticipant.id, name: otherParticipant.name }
          : null,
        headerDisplayName,
        isOtherParticipantSelf,
      };
      logger.debug('[ChallengeCard] finished challenge', diagnostic);
    }
  }, [
    challenge.id,
    challenge.groupId,
    challenge.creatorId,
    challenge.leader,
    challenge.participantIds,
    sectionBucket,
    currentUserId,
    participants,
    otherParticipant,
    headerDisplayName,
    isOtherParticipantSelf,
  ]);

  // Determine user role and permissions
  const isCreator = challenge.creatorId === currentUserId;
  const currentUserParticipantStatus = challenge.participantStatuses?.find((p) => p.userId === currentUserId)?.status;
  const isPendingParticipant = !isCreator && currentUserParticipantStatus === 'PENDING';
  const insufficientChallengeCoins =
    isPendingParticipant && !hasEnoughCoins(userCoins, challenge.stakeCoins);

  // Check if challenge can be cancelled
  const canCancel = canCancelChallenge(challenge, currentUserId);

  // Manage challenge actions
  const { isUpdating, handleCancelChallenge, handleDeclineChallenge, handleUpdatePredictions } = useChallengeActions({
    challenge,
    currentUserId,
    userPredictions,
    isPendingParticipant,
    onShowMessage,
    onChallengeCancelled,
  });

  const { showAlert } = useDialog();
  const apiClient = useApiClient();
  const apiClientRef = useRef(apiClient);
  apiClientRef.current = apiClient;

  const [isReminding, setIsReminding] = useState(false);
  const handleRemindPress = useCallback(async () => {
    setIsReminding(true);
    try {
      const result = await remindChallenge(challenge.id, apiClientRef.current);
      if (result.success) {
        onShowMessage?.({
          message: 'Reminder sent',
          variant: 'success',
        });
        return;
      }
      if (result.retryAfterMinutes != null) {
        await showAlert({
          title: 'Remind cooldown',
          message: `You can send another reminder in ${result.retryAfterMinutes} minute${result.retryAfterMinutes === 1 ? '' : 's'}.`,
          type: 'default',
        });
        return;
      }
      onShowMessage?.({
        message: result.error || 'Failed to send reminder',
        variant: 'error',
      });
    } finally {
      setIsReminding(false);
    }
  }, [challenge.id, onShowMessage, showAlert]);

  const [picksModalVisible, setPicksModalVisible] = useState(false);

  useFocusEffect(
    useCallback(() => {
      return () => {
        setPicksModalVisible(false);
      };
    }, []),
  );

  const handlePicksModalSuccess = useCallback(() => {
    void loadChallenges?.();
  }, [loadChallenges]);

  const firstGameDate = useMemo(() => {
    const showCountdown =
      sectionBucket === 'yourMove' ||
      sectionBucket === 'active' ||
      sectionBucket === 'waitingForFriends';
    if (!showCountdown) return null;
    const start = challenge.dateRange?.start;
    if (start != null) return start.toISOString();
    if (!matches.length) return null;
    type MatchWithDate = { date: string };
    const sorted = ([...matches] as MatchWithDate[]).sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    return sorted[0]?.date ?? null;
  }, [sectionBucket, challenge.dateRange?.start, matches]);

  const isFirstGameStarted = useMemo(() => {
    if (!firstGameDate) return false;
    return new Date(firstGameDate).getTime() <= Date.now();
  }, [firstGameDate]);

  const isFinished = sectionBucket === 'finished';

  const finishedAtLabel = useMemo(() => {
    if (!isFinished) return null;
    const end = challenge.dateRange?.end ?? (challenge as { updatedAt?: Date }).updatedAt;
    if (!end) return null;
    const iso = typeof end === 'string' ? end : end instanceof Date ? end.toISOString() : null;
    return iso ? formatRelativeTime(iso) : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFinished, challenge.dateRange?.end, (challenge as { updatedAt?: Date }).updatedAt]);

  const creatorLabel = useMemo(() => {
    if (!isFinished) return null;
    const creator = participants.find(
      (p) => normalizeUserIdForComparison(p.id) === normalizeUserIdForComparison(challenge.creatorId)
    );
    if (!creator) return null;
    return creator.isCurrentUser ? 'You' : creator.name;
  }, [isFinished, participants, challenge.creatorId]);

  const leader = challenge.leader;
  const myPoints = challenge.myPoints;
  const didWin =
    isFinished && leader != null && leader.userId === currentUserId;
  const didDraw =
    isFinished &&
    (leader == null
      ? true
      : myPoints != null &&
        Number(myPoints) === Number(leader.points) &&
        leader.userId !== currentUserId);
  const cardStyle = useMemo(() => {
    if (isFinished) {
      if (didWin) return [styles.card, styles.cardFinishedWin];
      if (didDraw) return [styles.card, styles.cardFinishedDraw];
      return [styles.card, styles.cardFinishedLoss];
    }
    if (sectionBucket === 'yourMove') return [styles.card, styles.cardYourMove];
    if (sectionBucket === 'live') return [styles.card, styles.cardLive];
    if (sectionBucket === 'active') return [styles.card, styles.cardActive];
    if (sectionBucket === 'waitingForFriends' || sectionBucket === 'waitingForOpponent') return [styles.card, styles.cardWaiting];
    return styles.card;
  }, [isFinished, didWin, didDraw, sectionBucket, styles]);

  const showStatusBanner = sectionBucket === 'yourMove' || (sectionBucket === 'live') || showLiveMatchBadge;
  const statusBannerVariant: 'yourMove' | 'live' =
    sectionBucket === 'live' || showLiveMatchBadge ? 'live' : 'yourMove';

  const showHero =
    !isFinished &&
    sectionBucket !== 'waitingForFriends' &&
    sectionBucket !== 'waitingForOpponent' &&
    headerOpponentAvatarId != null;

  return (
    <>
      <View style={cardStyle} testID={testID || `challenge-card-${challenge.id}`}>
        {showStatusBanner && (
          <ChallengeCardStatusBanner variant={statusBannerVariant} />
        )}
        <SoundPressable
          tapSoundId={
            isExpanded ? SOUND_IDS.UI_SECTION_COLLAPSE : SOUND_IDS.UI_SECTION_EXPAND
          }
          onPress={toggleExpand}
          accessibilityRole="button"
          accessibilityState={{ expanded: isExpanded }}
          accessibilityLabel={buildChallengeCardA11yLabel(headerDisplayName, isExpanded)}
          style={({ pressed }) => [
            styles.headerTouchable,
            pressed && styles.headerTouchablePressed,
          ]}
        >
          {showHero && (
            <ChallengeCardHero
              currentUserAvatarId={currentUserAvatarId}
              currentUserAvatarConfig={currentUserAvatarConfig}
              opponentAvatarId={headerOpponentAvatarId}
              opponentAvatarConfig={headerOpponentAvatarConfig}
              opponentName={headerDisplayName}
              sectionBucket={sectionBucket}
              stakeCoins={challenge.stakeCoins}
              hasBannerAbove={showStatusBanner}
              firstGameDate={firstGameDate}
            />
          )}
          {!showHero && (
            <ChallengeCardHeader
            challenge={challenge}
            currentUserId={currentUserId}
            displayName={headerDisplayName}
            groupImageSource={groupImageSource}
            otherParticipantAvatar={headerOpponentAvatar}
            otherParticipantAvatarId={headerOpponentAvatarId}
            otherParticipantAvatarConfig={headerOpponentAvatarConfig}
            isExpanded={isExpanded}
            variant={variant}
            sectionBucket={sectionBucket}
            firstGameDate={firstGameDate}
            onCancelPress={
              variant === 'home' &&
              sectionBucket === 'waitingForFriends' &&
              !isGuestPendingArenaCard
                ? handleCancelChallenge
                : undefined
            }
            styles={styles}
            theme={theme}
            stakeCoins={challenge.stakeCoins}
            participantCount={challenge.participantCount}
            showLiveMatchBadge={showLiveMatchBadge}
          />
          )}
        </SoundPressable>
        <Animated.View style={[styles.expandedAnimatedOuter, expandedContentStyle]}>
          <View style={styles.expandedContentWrap}>
            <ScrollView
              style={styles.expandedScrollContent}
              nestedScrollEnabled
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
            >
              {challenge.groupId && otherParticipants.length > 0 && (
                <ChallengeParticipantSwitcher
                  participants={otherParticipants}
                  selectedOpponentId={effectiveSelectedOpponentId}
                  onSelectParticipant={setSelectedOpponentId}
                  testID={testID ? `${testID}-participant-switcher` : undefined}
                />
              )}
              <ChallengeCardMatches
                styles={styles}
                matches={matches as React.ComponentProps<typeof ChallengeCardMatches>['matches']}
                userPredictions={userPredictions}
                otherPicks={otherPicksFromPicks}
                otherParticipantPicks={otherParticipantPicks}
                opponent1x2ByMatch={opponent1x2ByMatch}
                my1x2OddByMatch={my1x2OddByMatch}
                my1x2CorrectByMatch={my1x2CorrectByMatch}
                currentUserId={currentUserId}
                showAllMatches={showAllMatches}
                onShowAllMatches={handleShowAllMatches}
                requiredMarketsByMatch={requiredMarketsByMatch}
                oddsSnapshot={challenge.oddsSnapshot}
                showRequiredMarketsOnly={isPendingParticipant}
                onOpenPicksModal={
                  isGuestPendingArenaCard
                    ? () => router.push(guestSignUpHref as `/${string}`)
                    : isPendingParticipant && !insufficientChallengeCoins
                    ? () => setPicksModalVisible(true)
                    : undefined
                }
              />
            </ScrollView>

            {isFinished && (creatorLabel != null || finishedAtLabel != null) && (
              <View style={styles.expandedCardFooter}>
                <View style={styles.expandedCardFooterItem}>
                  {creatorLabel != null && (
                    <Text style={styles.expandedCardFooterText}>Created by {creatorLabel}</Text>
                  )}
                </View>
                <View style={styles.expandedCardFooterItem}>
                  {finishedAtLabel != null && (
                    <Text style={styles.expandedCardFooterText}>{finishedAtLabel}</Text>
                  )}
                </View>
              </View>
            )}

            {sectionBucket !== 'finished' && (
              <ChallengeCardActions
                styles={styles}
                canCancelChallenge={canCancel}
                isPendingParticipant={isPendingParticipant}
                insufficientCoins={insufficientChallengeCoins}
                hasChanges={hasChanges}
                isUpdating={isUpdating}
                isFirstGameStarted={isFirstGameStarted}
                onCancelChallenge={handleCancelChallenge}
                onDeclineChallenge={handleDeclineChallenge}
                onOpenPicksModal={
                  isGuestPendingArenaCard
                    ? () => router.push(guestSignUpHref as `/${string}`)
                    : () => setPicksModalVisible(true)
                }
                onUpdatePredictions={handleUpdatePredictions}
                primaryCtaLabelOverride={
                  isGuestPendingArenaCard ? GUEST_PENDING_ARENA_PRIMARY_CTA : undefined
                }
                onRemindPress={
                  variant === 'home' &&
                  sectionBucket === 'waitingForFriends' &&
                  !isGuestPendingArenaCard &&
                  !isFirstGameStarted
                    ? handleRemindPress
                    : undefined
                }
                isReminding={isReminding}
              />
            )}
          </View>
        </Animated.View>
      </View>

      <AcceptChallengePicksModal
        visible={picksModalVisible}
        onClose={() => setPicksModalVisible(false)}
        challenge={challenge}
        currentUserId={currentUserId}
        matches={matches as MatchApiResponse[]}
        firstGameDate={firstGameDate}
        insufficientCoins={insufficientChallengeCoins}
        onShowMessage={onShowMessage}
        onSuccess={handlePicksModalSuccess}
      />
    </>
  );
};

